/*
  User facing transaction type label

  User can edit
*/
import { useState, useEffect, useMemo } from 'react'

import { updateUserTransaction } from '@/featuresV1/Transactions/transactions.slice'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'
import { Select, Tooltip } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsLockedTransaction } from '@/featuresV1/Transactions/transactions.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'
import { isFreeTrialPromoCode } from '@/selectors/user.selectors'
import { TRANSACTION_TYPE_OPTIONS } from '@/constants/transactionConstants'

interface EditableTransactionTypeLabelProps {
  transaction: TransactionOrSplit
  /*Used to denote that we should default to let users edit*/
  review?: boolean
}

export const EditableTransactionTypeLabel = ({
  transaction,
  review = false,
}: EditableTransactionTypeLabelProps) => {
  const dispatch = useAppDispatch()
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)
  const locked = useReselector(
    selectIsLockedTransaction,
    transaction.splitFrom || transaction.id
  )
  const [transactionType, setTransactionType] = useState<
    'personal' | 'business' | 'split'
  >()
  const [updating, setUpdating] = useState(false)
  const popupContent = useMemo(() => {
    if (!review) {
      if (transaction.type) {
        return 'Did we miscategorize this transaction? Click to recategorize.'
      } else {
        return 'No action needed right now! Our team reviews your transactions on a monthly basis and will categorize this for you.'
      }
    }
    return null
  }, [transaction.type, review])

  useEffect(() => {
    setTransactionType(transaction.type || undefined)
  }, [transaction])

  const updateTransactionType = async (
    value: TransactionRuleCategoryType | undefined
  ) => {
    await setTransactionType(value)
    await setUpdating(true)
    const updated = await updateUserTransaction(transaction.id, {
      type: value,
      excludedAt: value === 'personal' ? Date.now() : null,
    })(dispatch)
    if (updated) {
      await setUpdating(false)
    }
  }

  if (hasFreeTrialPromoCode) {
    return (
      <Select
        variant="text"
        placeholder="Unselected"
        value={transactionType}
        options={TRANSACTION_TYPE_OPTIONS}
        onValueChange={(value) =>
          updateTransactionType(value as TransactionRuleCategoryType)
        }
        disabled={updating || locked}
        withTrigger
      />
    )
  }

  return (
    <div className="max-w-[250px] w-full">
      {/* Preserve the behavior of the tooltip open state prevention if we are on the review screen */}
      <Tooltip open={review || locked ? false : undefined}>
        <Select
          value={transactionType}
          variant="text"
          onValueChange={(value) =>
            updateTransactionType(value as TransactionRuleCategoryType)
          }
          disabled={updating || locked}
        >
          <Tooltip.Trigger asChild>
            <Select.Trigger variant="text">
              <Select.Value placeholder="Unselected" />
            </Select.Trigger>
          </Tooltip.Trigger>

          <Select.Content variant="text">
            {TRANSACTION_TYPE_OPTIONS.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.text}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>

        <Tooltip.Content
          side="bottom"
          align="start"
          className="max-w-[250px] bg-neutral-700 text-neutral-0 p-2 rounded-md mt-2"
        >
          {popupContent}
        </Tooltip.Content>
      </Tooltip>
    </div>
  )
}
